let currentHubPageContext = null;

const MAX_ITEMS = 10;
const MAX_TEXT = 2200;

function compact(value) {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return String(value);
}

function truncate(value, max = MAX_TEXT) {
  const text = compact(value).replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 3)).trim()}...`;
}

async function fetchJson(path, signal) {
  try {
    const res = await fetch(path, { credentials: 'same-origin', signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

let hubFeatureCache = null;

async function getHubFeatures(signal) {
  if (hubFeatureCache) return hubFeatureCache;
  const status = await fetchJson('/api/status', signal);
  hubFeatureCache = status?.features && typeof status.features === 'object' ? status.features : {};
  return hubFeatureCache;
}

async function isFeatureEnabled(name, signal) {
  const features = await getHubFeatures(signal);
  return features?.[name] !== false;
}

function listText(items, formatter, emptyText) {
  if (!Array.isArray(items) || !items.length) return emptyText;
  return items.slice(0, MAX_ITEMS).map(formatter).filter(Boolean).join('\n');
}

function encodeDocPath(path) {
  return String(path || '')
    .split('/')
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

function getDocPath(pathname) {
  if (pathname === '/docs' || pathname === '/docs/') return '';
  if (!pathname.startsWith('/docs/')) return '';
  return pathname
    .slice('/docs/'.length)
    .split('/')
    .filter(Boolean)
    .map((seg) => {
      try { return decodeURIComponent(seg); } catch { return seg; }
    })
    .join('/');
}

function personName(person) {
  return person?.display_name || person?.name || person?.username || person?.email || `Person ${person?.id || ''}`.trim();
}

function eventTitle(event) {
  return event?.title || event?.name || `Event ${event?.id || ''}`.trim();
}

function docTitle(doc) {
  return doc?.title || doc?.path || doc?.id || 'Document';
}

function imageTitle(image) {
  return image?.title || image?.filename || image?.id || 'Image';
}

function fileTitle(file) {
  return file?.title || file?.filename || file?.id || 'File';
}

function buildContext({ kind, label, summary, details, sourceUrl }) {
  const prompt = [
    'Hidden Hub page context for the next assistant response.',
    'Use this context when it is relevant to the user request.',
    'Do not mention that this context was injected, hidden, or system-provided unless the user explicitly asks how context works.',
    `Current page: ${label}`,
    `URL: ${sourceUrl}`,
    summary ? `Summary: ${summary}` : '',
    details ? `Details:\n${truncate(details)}` : '',
  ].filter(Boolean).join('\n\n');

  return {
    kind,
    label,
    summary: truncate(summary || label, 180),
    source_url: sourceUrl,
    prompt,
  };
}

export function buildInjectedHubPageContext(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const sourceUrl = truncate(
    payload.source_url || payload.sourceUrl || payload.url || payload.href || 'embedded-host',
    500,
  );
  const label = truncate(payload.label || payload.title || payload.page || 'Embedded page', 180);
  const summary = truncate(payload.summary || payload.description || label, 500);
  const details = [
    payload.details,
    payload.path ? `Path: ${payload.path}` : '',
    payload.pathname ? `Pathname: ${payload.pathname}` : '',
    payload.object_type ? `Object type: ${payload.object_type}` : '',
    payload.object_id ? `Object id: ${payload.object_id}` : '',
    payload.object_name ? `Object name: ${payload.object_name}` : '',
  ].filter(Boolean).join('\n');

  return {
    ...buildContext({
      kind: truncate(payload.kind || payload.type || 'embedded', 80),
      label,
      summary,
      details,
      sourceUrl,
    }),
    embedded: true,
    source: payload.source || 'host',
    desktop_available: Boolean(
      payload.desktop_available ||
      payload.desktopAvailable ||
      payload.show_desktop ||
      payload.showDesktop,
    ),
  };
}

function formatPerson(person) {
  const bits = [
    personName(person),
    person?.id ? `id: ${compact(person.id)}` : '',
    compact(person?.title || person?.role),
    compact(person?.company || person?.organization),
    compact(person?.email),
    compact(person?.labels),
  ].filter(Boolean);
  return `- ${bits.join(' | ')}`;
}

function formatEvent(event) {
  const bits = [
    eventTitle(event),
    event?.id ? `id: ${compact(event.id)}` : '',
    compact(event?.start || event?.date),
    compact(event?.location),
    compact(event?.status),
    compact(event?.labels),
  ].filter(Boolean);
  return `- ${bits.join(' | ')}`;
}

function formatDoc(doc) {
  const bits = [
    docTitle(doc),
    compact(doc?.path || doc?.id),
    compact(doc?.updated || doc?.created),
    compact(doc?.author),
  ].filter(Boolean);
  return `- ${bits.join(' | ')}`;
}

function formatImage(image) {
  const bits = [
    imageTitle(image),
    image?.id ? `id: ${compact(image.id)}` : '',
    compact(image?.folder),
    compact(image?.source),
    compact(image?.url),
  ].filter(Boolean);
  return `- ${bits.join(' | ')}`;
}

function formatFile(file) {
  const bits = [
    fileTitle(file),
    file?.id ? `id: ${compact(file.id)}` : '',
    compact(file?.filename),
    compact(file?.folder || 'Files'),
    compact(file?.mime_type),
    compact(file?.size ? `${file.size} bytes` : ''),
    compact(file?.tags),
    compact(file?.description),
  ].filter(Boolean);
  return `- ${bits.join(' | ')}`;
}

async function collectPeopleContext(sourceUrl, signal) {
  const people = await fetchJson('/api/people', signal);
  const count = Array.isArray(people) ? people.length : 0;
  return buildContext({
    kind: 'people',
    label: 'Viewing People',
    summary: count ? `${count} people in this workspace.` : 'The People directory is open.',
    sourceUrl,
    details: listText(people, formatPerson, 'No people are currently available from the Hub API.'),
  });
}

async function collectPersonContext(id, sourceUrl, signal) {
  const [person, notes, messages] = await Promise.all([
    fetchJson(`/api/people/${encodeURIComponent(id)}`, signal),
    fetchJson(`/api/people/${encodeURIComponent(id)}/notes`, signal),
    fetchJson(`/api/people/${encodeURIComponent(id)}/messages`, signal),
  ]);
  if (!person) return collectPeopleContext(sourceUrl, signal);

  const details = [
    formatPerson(person),
    person?.bio || person?.notes ? `Bio/notes: ${compact(person.bio || person.notes)}` : '',
    listText(notes, (n) => `- Note by ${compact(n.author || n.created_by || 'unknown')}: ${compact(n.body || n.text)}`, ''),
    listText(messages, (m) => `- Message by ${compact(m.author || m.from || 'unknown')}: ${compact(m.body || m.text || m.content)}`, ''),
  ].filter(Boolean).join('\n');

  return buildContext({
    kind: 'person',
    label: `Person: ${personName(person)}`,
    summary: [personName(person), person?.title || person?.role, person?.company || person?.organization].filter(Boolean).join(' | '),
    sourceUrl,
    details,
  });
}

async function collectEventsContext(sourceUrl, signal) {
  const events = await fetchJson('/api/events', signal);
  const count = Array.isArray(events) ? events.length : 0;
  return buildContext({
    kind: 'events',
    label: 'Viewing Events',
    summary: count ? `${count} events in this workspace.` : 'The Events page is open.',
    sourceUrl,
    details: listText(events, formatEvent, 'No events are currently available from the Hub API.'),
  });
}

async function collectEventContext(id, sourceUrl, signal) {
  const [event, attendees, messages] = await Promise.all([
    fetchJson(`/api/events/${encodeURIComponent(id)}`, signal),
    fetchJson(`/api/events/${encodeURIComponent(id)}/attendees`, signal),
    fetchJson(`/api/events/${encodeURIComponent(id)}/messages`, signal),
  ]);
  if (!event) return collectEventsContext(sourceUrl, signal);
  let activeTab = '';
  try {
    activeTab = window._hubEventDetail?.activeTab || '';
  } catch {}

  const details = [
    formatEvent(event),
    activeTab ? `Active event tab: ${activeTab}` : '',
    event?.description ? `Description: ${event.description}` : '',
    listText(attendees, (a) => `- Attendee: ${compact(a.name || a.display_name || a.email || a.person_id || a.id)} ${compact(a.status)}`.trim(), ''),
    listText(messages, (m) => `- Message by ${compact(m.author || m.from || 'unknown')}: ${compact(m.body || m.text || m.content)}`, ''),
  ].filter(Boolean).join('\n');

  return buildContext({
    kind: 'event',
    label: `Event: ${eventTitle(event)}`,
    summary: [eventTitle(event), event?.start || event?.date, event?.location].filter(Boolean).join(' | '),
    sourceUrl,
    details,
  });
}

async function collectDocsContext(sourceUrl, signal) {
  const docs = await fetchJson('/api/docs', signal);
  const count = Array.isArray(docs) ? docs.length : 0;
  return buildContext({
    kind: 'docs',
    label: 'Viewing Docs',
    summary: count ? `${count} documents in this workspace.` : 'The Docs page is open.',
    sourceUrl,
    details: listText(docs, formatDoc, 'No docs are currently available from the Hub API.'),
  });
}

async function collectDocumentContext(path, sourceUrl, signal) {
  const encoded = encodeDocPath(path || 'index');
  const doc = await fetchJson(`/api/docs/${encoded}`, signal);
  if (!doc) return collectDocsContext(sourceUrl, signal);

  const details = [
    formatDoc(doc),
    doc?.content || doc?.body || doc?.markdown || '',
  ].filter(Boolean).join('\n');

  return buildContext({
    kind: 'doc',
    label: `Doc: ${docTitle(doc)}`,
    summary: [docTitle(doc), doc?.path || path, doc?.updated || doc?.created].filter(Boolean).join(' | '),
    sourceUrl,
    details,
  });
}

async function collectGalleryContext(sourceUrl, signal) {
  const [images, folders] = await Promise.all([
    fetchJson('/api/gallery', signal),
    fetchJson('/api/gallery/folders', signal),
  ]);
  const count = Array.isArray(images) ? images.length : 0;
  const folderText = Array.isArray(folders) && folders.length ? `Folders: ${folders.slice(0, MAX_ITEMS).join(', ')}` : '';
  return buildContext({
    kind: 'gallery',
    label: 'Viewing Gallery',
    summary: count ? `${count} gallery images available.` : 'The Gallery page is open.',
    sourceUrl,
    details: [folderText, listText(images, formatImage, 'No images are currently available from the Hub API.')].filter(Boolean).join('\n'),
  });
}

async function collectGalleryItemContext(id, sourceUrl, signal) {
  const image = await fetchJson(`/api/gallery/${encodeURIComponent(id)}`, signal);
  if (!image) return collectGalleryContext(sourceUrl, signal);
  return buildContext({
    kind: 'gallery_item',
    label: `Gallery: ${imageTitle(image)}`,
    summary: [imageTitle(image), image?.folder, image?.source, image?.url].filter(Boolean).join(' | '),
    sourceUrl,
    details: formatImage(image),
  });
}

async function collectFilesContext(sourceUrl, signal) {
  const [files, folders] = await Promise.all([
    fetchJson('/api/files', signal),
    fetchJson('/api/files/folders', signal),
  ]);
  const fileCount = Array.isArray(files) ? files.length : 0;
  const folderCount = Array.isArray(folders) ? folders.length : 0;
  const folderText = folderCount ? `Folders: ${folders.slice(0, MAX_ITEMS).join(', ')}` : '';

  return buildContext({
    kind: 'files',
    label: 'Viewing Files',
    summary: fileCount || folderCount
      ? `${fileCount} files and ${folderCount} folders available.`
      : 'The Files page is open.',
    sourceUrl,
    details: [
      folderText,
      listText(files, formatFile, 'No files are currently available from the Hub API.'),
    ].filter(Boolean).join('\n'),
  });
}

async function collectFileContext(id, sourceUrl, signal) {
  const file = await fetchJson(`/api/files/${encodeURIComponent(id)}`, signal);
  if (!file) return collectFilesContext(sourceUrl, signal);
  return buildContext({
    kind: 'file',
    label: `File: ${fileTitle(file)}`,
    summary: [fileTitle(file), file?.folder, file?.mime_type, file?.size ? `${file.size} bytes` : ''].filter(Boolean).join(' | '),
    sourceUrl,
    details: formatFile(file),
  });
}

async function collectScheduledTasksContext(sourceUrl, signal) {
  if (!(await isFeatureEnabled('scheduled_tasks', signal))) return null;
  const jobs = await fetchJson('/api/cron', signal);
  const list = Array.isArray(jobs?.jobs) ? jobs.jobs : Array.isArray(jobs) ? jobs : [];
  return buildContext({
    kind: 'scheduled_tasks',
    label: 'Viewing Scheduled Tasks',
    summary: list.length ? `${list.length} scheduled tasks available.` : 'The Scheduled Tasks page is open.',
    sourceUrl,
    details: listText(
      list,
      (job) => `- ${compact(job.name || job.id)} | ${compact(job.schedule || job.cron || job.at)} | ${job.enabled === false ? 'disabled' : 'enabled'}`,
      'No scheduled tasks are currently available from the Hub API.',
    ),
  });
}

async function collectReviewBoardContext(sourceUrl, signal) {
  if (!(await isFeatureEnabled('human_review', signal))) return null;
  const reviews = await fetchJson('/api/reviews', signal);
  const list = Array.isArray(reviews?.items) ? reviews.items : Array.isArray(reviews) ? reviews : [];
  return buildContext({
    kind: 'human_review',
    label: 'Viewing Human Review',
    summary: list.length ? `${list.length} review items available.` : 'The Human Review page is open.',
    sourceUrl,
    details: listText(
      list,
      (review) => `- ${compact(review.title || review.name || review.id)} | ${compact(review.status)} | ${compact(review.kind || review.type)}`,
      'No review items are currently available from the Hub API.',
    ),
  });
}

async function collectLimitsContext(sourceUrl, signal) {
  if (!(await isFeatureEnabled('limits', signal))) return null;
  return buildContext({
    kind: 'limits',
    label: 'Viewing Limits',
    summary: 'The Limits page is open.',
    sourceUrl,
    details: 'Limits controls are available for configured Hub channels.',
  });
}

export async function collectHubPageContext(signal) {
  if (typeof window === 'undefined') return null;
  const { pathname, href } = window.location;

  const personMatch = pathname.match(/^\/people\/([^/]+)/);
  if (personMatch) return collectPersonContext(decodeURIComponent(personMatch[1]), href, signal);
  if (pathname === '/people' || pathname === '/people/') return collectPeopleContext(href, signal);

  const eventMatch = pathname.match(/^\/events\/([^/]+)/);
  if (eventMatch) return collectEventContext(decodeURIComponent(eventMatch[1]), href, signal);
  if (pathname === '/events' || pathname === '/events/') return collectEventsContext(href, signal);

  if (pathname === '/docs' || pathname === '/docs/' || pathname.startsWith('/docs/')) {
    const docPath = getDocPath(pathname);
    return docPath ? collectDocumentContext(docPath, href, signal) : collectDocsContext(href, signal);
  }

  const galleryMatch = pathname.match(/^\/gallery\/([^/]+)/);
  if (galleryMatch) return collectGalleryItemContext(decodeURIComponent(galleryMatch[1]), href, signal);
  if (pathname === '/gallery' || pathname === '/gallery/') return collectGalleryContext(href, signal);

  const fileMatch = pathname.match(/^\/files\/([^/]+)/);
  if (fileMatch) return collectFileContext(decodeURIComponent(fileMatch[1]), href, signal);
  if (pathname === '/files' || pathname === '/files/') return collectFilesContext(href, signal);

  if (pathname === '/scheduled-tasks' || pathname === '/scheduled-tasks/') return collectScheduledTasksContext(href, signal);
  if (pathname === '/standing-orders' || pathname === '/standing-orders/') return collectScheduledTasksContext(href, signal);
  if (pathname === '/review-board' || pathname === '/review-board/') return collectReviewBoardContext(href, signal);
  if (pathname === '/limits' || pathname === '/limits/') return collectLimitsContext(href, signal);
  return null;
}

export function setCurrentHubPageContext(context) {
  currentHubPageContext = context || null;
}

export function getCurrentHubPageContext() {
  return currentHubPageContext;
}
